import Bugsnag from '@bugsnag/js';

export enum ErrorType {
  exec,
  permission,
  other,
}

function setStyles(element: HTMLElement) {
  element.style.all = 'unset';
  element.style.position = 'absolute';
  element.style.top = '-99999px';
  element.style.left = '-99999px';
  element.style.clip = 'rect(0, 0, 0, 0)';
}

async function navigatorReadText(): Promise<void> {
  return new Promise((resolve, reject) => {
    const errorHandler = async (error) => {
      const { state } = await navigator.permissions.query({
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        name: 'clipboard-read',
      });
      Bugsnag.notify(error);

      return reject({
        type: state === 'granted' ? ErrorType.other : ErrorType.permission,
        error,
      });
    };

    try {
      navigator.clipboard.readText().then(() => resolve(), errorHandler);
    } catch (err) {
      errorHandler(err);
    }
  });
}

function navigatorWriteData(data: ClipboardItem[]): Promise<void> {
  return new Promise((resolve, reject) => {
    const errorHandler = async (error) => {
      const { state } = await navigator.permissions.query({
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        name: 'clipboard-write',
      });
      Bugsnag.notify(error);

      return reject({
        type: state === 'granted' ? ErrorType.other : ErrorType.permission,
        error,
      });
    };

    try {
      navigator.clipboard.write(data).then(() => resolve(), errorHandler);
    } catch (err) {
      errorHandler(err);
    }
  });
}

export function clipboardRead() {
  if (typeof window !== 'object') {
    return;
  }

  if (navigator?.clipboard?.readText && navigator?.permissions?.query) {
    return navigatorReadText();
  }

  const textarea = document.createElement('textarea');

  setStyles(textarea);
  document.body.appendChild(textarea);
  textarea.focus();

  try {
    const success = document.execCommand('paste');

    if (!success) {
      Bugsnag.notify('exec paste command');
      return Promise.reject({
        type: ErrorType.exec,
        error: new Error('exec paste command'),
      });
    }

    const str = textarea.value;

    textarea.remove();

    return Promise.resolve(str);
  } catch (error) {
    Bugsnag.notify(error);
    return Promise.reject({ type: ErrorType.exec, error });
  }
}

function createClipboardItem(text: string, html?: string) {
  const textPlainType = 'text/plain';
  const textPlainBlob = new Blob([text], { type: textPlainType });
  const textHtmlType = 'text/html';
  const textHtmlBlob = new Blob([html], { type: textHtmlType });
  const item = { [textPlainType]: textPlainBlob };

  return html ? Object.assign(item, { [textHtmlType]: textHtmlBlob }) : item;
}

export async function clipboardWrite(text: string, html?: string) {
  if (typeof window !== 'object') {
    return;
  }

  if (navigator?.clipboard?.write && navigator?.permissions?.query && ClipboardItem) {
    return navigatorWriteData([new ClipboardItem(createClipboardItem(text, html))]);
  }

  const div = document.createElement('div');

  setStyles(div);
  div.textContent = text;

  const range = document.createRange();
  const selection = window.getSelection();

  selection?.removeAllRanges();
  document.body.appendChild(div);
  range.selectNodeContents(div);
  selection?.addRange(range);

  const e = { type: ErrorType.exec, error: new Error('exec copy command') };
  let success;

  try {
    success = document.execCommand('copy');
  } catch (err) {
    e.error = err;
    Bugsnag.notify(err);
  }

  div.remove();

  return success ? Promise.resolve() : Promise.reject(e);
}
